const post = {
  slug: "what-checkout-dm-really-costs-plan-gateway-fees",
  title: "What Checkout DM Really Costs: Plan, Gateway and Other Fees",
  seoTitle: "What Checkout DM Really Costs",
  description:
    "Checkout DM takes no commission, but you still pay a plan price plus your own gateway and shipping costs. See the full cost and when a flat plan wins.",
  excerpt:
    "A flat plan price is easy to see. The full cost has a few more parts. Here is every cost you will meet when selling with Checkout DM, and which ones Checkout DM does not charge.",
  category: "Product",
  tags: ["Checkout DM cost", "Checkout DM pricing", "Instagram selling fees India"],
  date: "2026-10-31T12:00:00Z",
  cover: {
    src: "/blog-images/what-checkout-dm-really-costs-plan-gateway-fees-cover.jpg",
    alt: "A phone listing plan price, zero commission, gateway fee and where payments go for Checkout DM",
  },
  blocks: [
    {
      type: "p",
      text:
        "Checkout DM has a clear price list, but the price of a plan is not the whole cost of selling. Payment fees and shipping matter too, and it helps to know which of them Checkout DM controls and which it does not. This guide lists every cost you will meet, shows a worked example and is honest about when a flat monthly plan is the more expensive choice.",
    },

    { type: "h2", text: "The three costs of selling with Checkout DM" },
    {
      type: "p",
      text:
        "When you sell through Instagram DMs with Checkout DM, money leaves your business in three places. Checkout DM only sets the first one.",
    },
    {
      type: "ol",
      items: [
        "**Your plan.** A flat monthly or yearly price, from \u20B90 on Free to \u20B96,499 a month on Pro.",
        "**Payment fees.** What your chosen payment method costs, if anything.",
        "**Shipping.** Your courier charges and packing materials.",
      ],
    },

    { type: "h2", text: "1. The plan price" },
    {
      type: "table",
      headers: ["Plan", "Monthly", "Yearly (10 months)", "About per month on yearly"],
      rows: [
        ["Free", "\u20B90", "\u2014", "\u2014"],
        ["Creator", "\u20B9499", "\u20B94,990", "\u20B9416"],
        ["Starter", "\u20B91,499", "\u20B914,990", "\u20B91,249"],
        ["Growth", "\u20B92,899", "\u20B928,990", "\u20B92,416"],
        ["Pro", "\u20B96,499", "\u20B964,990", "\u20B95,416"],
      ],
    },
    {
      type: "p",
      text:
        "Yearly billing gives you two months free, because you pay for ten months instead of twelve. Plans can also be shown in US dollars with the currency switch on the [Pricing page](/pricing): Creator $9, Starter $19, Growth $39 and Pro $69 a month. Always treat that page as the final word, since prices can change.",
    },

    { type: "h2", text: "2. Payment fees" },
    {
      type: "p",
      text:
        "This is the cost people most often miss, because it does not come from Checkout DM at all. It depends on how you choose to collect money:",
    },
    {
      type: "ul",
      items: [
        "**UPI, bank transfer or QR code with manual confirmation.** Payments land directly in your account. No payment gateway is involved, so there is no gateway fee, and you confirm each payment yourself before you ship.",
        "**Razorpay, Stripe or PayPal.** Payments are confirmed automatically. The gateway charges its own fee on each payment. Razorpay, Stripe and PayPal set those fees and can change them, and Checkout DM neither adds to them nor takes a share.",
      ],
    },
    {
      type: "p",
      text:
        "You can keep one automatic gateway active at a time. Because gateway rates change, check the current rate on the gateway\u2019s own site before you work out your margin. Our guides on [UPI versus a payment gateway](/blog/accept-payments-instagram-upi-vs-gateway) and [connecting Razorpay](/blog/connect-razorpay-to-checkout-dm) explain the trade-off between manual and automatic confirmation.",
    },

    { type: "h2", text: "3. Shipping and packing" },
    {
      type: "p",
      text:
        "Checkout DM does not ship your parcels, so courier charges are your own cost. What the tool does is let you add a shipping fee at checkout, on every plan including Free, so your customer can pay for shipping. From Starter you can also set automatic free shipping above a minimum cart value, which is a decision about your margin rather than a Checkout DM fee. For ways to keep this cost down, see our guide to [packing and shipping Instagram orders](/blog/pack-and-ship-instagram-orders).",
    },

    { type: "h2", text: "What Checkout DM does not charge" },
    {
      type: "ul",
      items: [
        "**No commission and no transaction fee** on any plan, on any order.",
        "**Nothing from your buyers.** This pricing is for merchants. Your customers never pay Checkout DM anything.",
        "**No share of your payments.** Payments go directly to your own payment account.",
      ],
    },

    { type: "h2", text: "A worked example: a flat plan versus a per-sale fee" },
    {
      type: "p",
      text:
        "A flat monthly plan and a percentage fee behave very differently as your sales change. The table compares Starter at \u20B91,499 a month with a platform that takes 2% of each sale. The 2% is only an illustration, not any named company\u2019s rate. Gateway fees are left out, because you would pay them either way.",
    },
    {
      type: "table",
      headers: ["Monthly sales", "2% of sales", "Checkout DM Starter (monthly)", "Cheaper option"],
      rows: [
        ["\u20B925,000", "\u20B9500", "\u20B91,499", "The 2% fee"],
        ["\u20B950,000", "\u20B91,000", "\u20B91,499", "The 2% fee"],
        ["\u20B91,00,000", "\u20B92,000", "\u20B91,499", "Starter"],
        ["\u20B92,00,000", "\u20B94,000", "\u20B91,499", "Starter"],
      ],
    },
    {
      type: "p",
      text:
        "The break-even is about \u20B975,000 of sales a month, which is \u20B91,499 divided by 0.02. Below that, a small percentage fee costs less than a flat plan. Above it, the flat plan wins, and the gap grows with every extra order. If your sales are still small, the Free plan lets you test at \u20B90 until the volume justifies a paid plan.",
    },
    {
      type: "callout",
      tone: "peach",
      title: "Do your own sum",
      text:
        "Replace the 2% with the real fee of whichever platform or marketplace you are comparing, add its subscription, and run the same table with your own monthly sales. Our guide to [how much it costs to sell on Instagram](/blog/how-much-does-it-cost-to-sell-on-instagram) walks through the rest of the costs.",
    },

    { type: "h2", text: "Cancelling, refunds and free days" },
    {
      type: "p",
      text:
        "You can cancel at any time from your dashboard. You keep full access to your current plan until the period you have already paid for ends, with no further charges after that. Subscription payments are non-refundable, including partial or unused periods, so choose yearly billing only when you are sure. Checkout DM also has a referral program that can earn you free days on your plan. See [how the referral program works](/blog/checkout-dm-referral-program-earn-free-days).",
    },

    { type: "h2", text: "The short version" },
    {
      type: "p",
      text:
        "Your monthly cost is your plan price, plus your gateway\u2019s fees if you use a gateway, plus your own shipping costs. Checkout DM sets only the first, and takes no commission on any order. To choose a plan, read [which Checkout DM plan you should choose](/blog/which-checkout-dm-plan-should-you-choose), or see every feature in [Checkout DM pricing and plans explained](/blog/checkout-dm-pricing-and-plans-explained).",
    },
  ],
  related: [
    { to: "/pricing", title: "Checkout DM pricing", description: "Current plans, prices and the full feature comparison." },
    { to: "/faq", title: "Frequently Asked Questions", description: "Answers on plans, payments, shipping and returns." },
    { to: "/help", title: "Help Guide", description: "How to set up and run your Checkout DM account." },
  ],
  faqs: [
    {
      q: "Does Checkout DM charge a commission on sales?",
      a: "No. There is no commission or transaction fee on any plan, and payments go directly to your own payment account.",
    },
    {
      q: "Will my customers pay anything to Checkout DM?",
      a: "No. The pricing is for merchants. Your buyers never pay Checkout DM anything.",
    },
    {
      q: "What fees will my payment gateway charge?",
      a: "That depends on the gateway. Razorpay, Stripe and PayPal each set their own fees, which Checkout DM neither controls nor adds to. Check the current rates on their websites.",
    },
    {
      q: "Is yearly billing cheaper?",
      a: "Yes. Yearly billing gives two months free, because you pay for ten months instead of twelve.",
    },
    {
      q: "Can I get a refund if I cancel?",
      a: "Subscription payments are non-refundable, including partial or unused periods. If you cancel, you keep using your plan until the period you have paid for ends.",
    },
  ],
};

export default post;
